import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "@headlessui/react";
import { PMButton } from "../../../utils/PMButton";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import ModernStorageSelector from "../../../utils/Selector/ModernStorageSelector";
import { useDispatch, useSelector } from "react-redux";
import { getStorages } from "../../../../slices/inventory/storage";
import toast from "react-hot-toast";

export const BulkMarkPickedDialog = ({ open, onClose, items, onConfirm }) => {
  const dispatch = useDispatch();
  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];
  const [storageSelectorOpen, setStorageSelectorOpen] = useState(false);
  const [selectedStorageId, setSelectedStorageId] = useState(null);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [needsStorage, setNeedsStorage] = useState(false);
  const prevOpenRef = useRef(false);

  // Filter out manual products (items without product_id) and only show unpicked, non-invoiced items
  const unpickedItems = items.filter((item) => !item.picked && !item.invoiced && item.product_id);

  // Check if any items need storage_id - only reset when dialog first opens
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    const isNowOpen = open;
    prevOpenRef.current = open;

    if (isNowOpen && !wasOpen) {
      // Dialog just opened (changed from closed to open)
      const itemsNeedingStorage = unpickedItems.filter((item) => !item.storage_id);
      console.log("[BulkMarkPickedDialog] Dialog just opened, items needing storage:", itemsNeedingStorage.length);
      console.log("[BulkMarkPickedDialog] Unpicked items:", unpickedItems);
      setNeedsStorage(itemsNeedingStorage.length > 0);
      // Reset storage selection when dialog first opens
      console.log("[BulkMarkPickedDialog] Resetting storage selection on dialog open");
      setSelectedStorageId(null);
      setSelectedStorage(null);
    } else if (isNowOpen && wasOpen) {
      // Dialog was already open, just update needsStorage if items changed
      const itemsNeedingStorage = unpickedItems.filter((item) => !item.storage_id);
      setNeedsStorage(itemsNeedingStorage.length > 0);
      console.log("[BulkMarkPickedDialog] Dialog already open, updating needsStorage:", itemsNeedingStorage.length > 0);
    }
  }, [open, unpickedItems]);

  // Load storages if not already loaded
  useEffect(() => {
    if (storages.length === 0) {
      dispatch(getStorages());
    }
  }, [dispatch, storages.length]);

  const handleConfirm = () => {
    console.log("[BulkMarkPickedDialog] handleConfirm called");
    console.log("[BulkMarkPickedDialog] needsStorage:", needsStorage);
    console.log("[BulkMarkPickedDialog] selectedStorageId:", selectedStorageId);
    console.log("[BulkMarkPickedDialog] selectedStorage:", selectedStorage);
    console.log("[BulkMarkPickedDialog] unpickedItems:", unpickedItems);
    
    // Re-check if any items actually need storage (items might have been updated)
    const itemsNeedingStorage = unpickedItems.filter((item) => !item.storage_id);
    const actuallyNeedsStorage = itemsNeedingStorage.length > 0;
    
    console.log("[BulkMarkPickedDialog] itemsNeedingStorage:", itemsNeedingStorage.length);
    console.log("[BulkMarkPickedDialog] actuallyNeedsStorage:", actuallyNeedsStorage);
    
    // Validate storage_id is selected if needed
    if (actuallyNeedsStorage && !selectedStorageId) {
      console.log("[BulkMarkPickedDialog] Validation failed: storage required but not selected");
      toast.error("Du må velge et varelager før du kan markere produkter som plukket");
      return; // Don't proceed if storage is required but not selected
    }
    
    console.log("[BulkMarkPickedDialog] Validation passed, calling onConfirm");
    onConfirm(unpickedItems, selectedStorageId);
    onClose();
    // Reset state
    setSelectedStorageId(null);
    setSelectedStorage(null);
    setNeedsStorage(false);
  };

  const handleClose = () => {
    onClose();
    setSelectedStorageId(null);
    setSelectedStorage(null);
    setNeedsStorage(false);
  };

  if (!open) return null;

  return (
    <>
      {/* Main Dialog */}
      <Dialog open={open} onClose={handleClose} className="relative z-[2100]">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity ${storageSelectorOpen ? 'pointer-events-none' : ''}`} 
          aria-hidden="true" 
        />

        {/* Dialog container */}
        <div className={`fixed inset-0 z-10 overflow-y-auto ${storageSelectorOpen ? 'pointer-events-none' : ''}`}>
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Dialog.Panel className={`relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 ${storageSelectorOpen ? 'pointer-events-none' : ''}`}>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <CheckCircleIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    Marker alle som plukket
                  </Dialog.Title>
                  <div className="mt-2">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-yellow-800">Viktig!</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            Dette markerer alle produkter som plukket (klargjort for oppdrag). 
                            Du kan senere registrere hvor mye som faktisk er brukt eller tatt med i bilen.
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {unpickedItems.length === 0 ? (
                        <span className="text-gray-600">Alle produkter er allerede markert som plukket.</span>
                      ) : (
                        <>
                          Dette vil markere <strong>{unpickedItems.length}</strong>{" "}
                          {unpickedItems.length === 1 ? "produkt" : "produkter"} som plukket:
                          <ul className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                            {unpickedItems.map((item) => (
                              <li key={item.id} className="text-sm text-gray-700 flex items-center">
                                <span className="mr-2">•</span>
                                <span>
                                  {item.product?.displayTitle || item.product?.title || item.title || `Produkt ${item.product_id}`}
                                  {" "}({item.quantity} {item.unit?.title || ""})
                                </span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Storage selection - required if items don't have storage_id */}
              {needsStorage && unpickedItems.length > 0 && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">
                        Varelager påkrevd
                      </h4>
                      <p className="text-sm text-yellow-700 mb-3">
                        Noen produkter mangler varelager. Du må velge et lager før du kan markere dem som plukket.
                      </p>
                      <div
                        className={`cursor-pointer border-2 rounded-lg p-3 transition-colors ${
                          selectedStorageId
                            ? "border-blue-300 bg-blue-50"
                            : "border-yellow-300 bg-white hover:border-blue-300"
                        }`}
                        onClick={() => {
                          console.log("[BulkMarkPickedDialog] Opening storage selector");
                          console.log("[BulkMarkPickedDialog] Current selectedStorageId:", selectedStorageId);
                          console.log("[BulkMarkPickedDialog] Current selectedStorage:", selectedStorage);
                          setStorageSelectorOpen(true);
                        }}
                      >
                        {(() => {
                          console.log("[BulkMarkPickedDialog] Rendering storage display");
                          console.log("[BulkMarkPickedDialog] selectedStorageId:", selectedStorageId);
                          console.log("[BulkMarkPickedDialog] selectedStorage:", selectedStorage);
                          console.log("[BulkMarkPickedDialog] storages array length:", storages.length);
                          
                          if (selectedStorageId && selectedStorage) {
                            return (
                              <div>
                                <div className="font-medium text-blue-700">
                                  {`${selectedStorage.storage_number ? `${selectedStorage.storage_number} - ` : ""}${selectedStorage.title || `Lager ${selectedStorage.id}`}`}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="text-gray-500 text-center">
                              <div className="text-sm font-medium">
                                + Klikk for å velge lager
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                {unpickedItems.length > 0 && (() => {
                  const isDisabled = needsStorage && !selectedStorageId;
                  console.log("[BulkMarkPickedDialog] Confirm button render");
                  console.log("[BulkMarkPickedDialog] isDisabled:", isDisabled);
                  console.log("[BulkMarkPickedDialog] needsStorage:", needsStorage);
                  console.log("[BulkMarkPickedDialog] selectedStorageId:", selectedStorageId);
                  
                  return (
                    <PMButton
                      onClick={handleConfirm}
                      disabled={isDisabled}
                      className="w-full sm:ml-3 sm:w-auto"
                    >
                      Bekreft - Marker alle som plukket
                    </PMButton>
                  );
                })()}
                <PMButton
                  variant="secondary"
                  onClick={handleClose}
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Avbryt
                </PMButton>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>

      {/* Storage Selector Dialog - Rendered via Portal to avoid z-index issues */}
      {storageSelectorOpen && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
          {/* Backdrop */}
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999
            }}
            onClick={() => setStorageSelectorOpen(false)}
            aria-hidden="true"
          />

          {/* Dialog container */}
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100000,
              overflowY: 'auto',
              pointerEvents: 'none'
            }}
          >
            <div style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              pointerEvents: 'none'
            }}>
              <div 
                style={{
                  position: 'relative',
                  backgroundColor: 'white',
                  borderRadius: '0.5rem',
                  padding: '1.5rem',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                  maxWidth: '28rem',
                  width: '100%',
                  pointerEvents: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ height: '500px', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
                  <ModernStorageSelector
                    open={storageSelectorOpen}
                    selectedStorage={selectedStorage || (selectedStorageId ? { id: selectedStorageId } : null)}
                    onChange={(storage) => {
                      console.log("[BulkMarkPickedDialog] ModernStorageSelector onChange called");
                      console.log("[BulkMarkPickedDialog] Storage received:", storage);
                      console.log("[BulkMarkPickedDialog] Storage type:", typeof storage);
                      console.log("[BulkMarkPickedDialog] Storage is array:", Array.isArray(storage));
                      
                      // ModernStorageSelector calls onChange with the storage object when selected
                      // For single selection (multiple=false), it passes the storage object or null
                      if (storage && storage.id) {
                        console.log("[BulkMarkPickedDialog] Setting storage ID:", storage.id);
                        console.log("[BulkMarkPickedDialog] Setting storage object:", storage);
                        setSelectedStorageId(storage.id);
                        setSelectedStorage(storage); // Store the full storage object
                        // ModernStorageSelector will auto-close after 300ms, but we close immediately for better UX
                        setTimeout(() => {
                          console.log("[BulkMarkPickedDialog] Closing storage selector");
                          setStorageSelectorOpen(false);
                        }, 100);
                      } else {
                        console.log("[BulkMarkPickedDialog] No storage or no ID, clearing selection");
                        setSelectedStorageId(null);
                        setSelectedStorage(null);
                      }
                    }}
                    onClose={() => {
                      setStorageSelectorOpen(false);
                    }}
                    multiple={false}
                    showHeader={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
